import * as Sentry from '@sentry/react';
import {
  AuthClient,
  type AuthOptions,
  createAuthClient,
} from '@storebrand-digital/auth';

import { isInternalUser, isLocalhost } from '@/config/utils';
import { getBasename } from '@/routes/utils/getBasename';
import { findOrganizationCmIdFromUrl } from '@/util/findOrganizationCmIdFromUrl';

import { Auth, TokenParsed } from './Auth';
import { AuthError } from './types/AuthError';
import { getOrganizationCmIdFromToken } from './utils/getOrganizationCmIdFromToken';

let auth: AuthClient | undefined;

async function initialize(): Promise<void> {
  const organizationCmId = findOrganizationCmIdFromUrl();

  const scope = organizationCmId
    ? `storebrand.private on_behalf_of:cm_id:${organizationCmId}`
    : 'storebrand.private';

  const options: AuthOptions = {
    config: {
      realm: window.config.keycloakRealm,
      clientId: window.config.keycloakClientId,
      url: window.config.keycloakUrl,
    },
    init: {
      scope,
      silentCheckSsoRedirectUri: `${window.location.origin}/.well-known/openid-silent-check-sso.html`,
    },
    login: {
      scope,
      allowedACR: [
        'https://id.storebrand.no/authn/al3',
        'https://id.storebrand.no/authn/al4',
      ],
      allowedAMR: [
        'no:bankid:regular',
        'pwd:mfa',
        'se:bankid:regular',
        'se:bankid:mobile',
        'dk:eid:mitid',
      ],
      showExtraIdpList: true,
    },
    // Note: The logout config is defined in the logout function below.
  };

  auth = createAuthClient(options);

  // Try to initialize KeyCloak and trigger login.
  try {
    await auth.init();

    if (!auth.authenticated) {
      await auth.login();
    }
  } catch (error) {
    const authError = error as AuthError;

    if (isLocalhost()) {
      // Something went wrong when initializing the authentication.
      // The user may have cancelled the authentication process or there was an error.
      // Only log the error in the console when running on localhost, otherwise it will
      // be logged in Sentry.
      console.error('Failed to initialize authentication', authError?.message);
    }

    // Log the error in Sentry only if the error code is not interaction_required
    // as this error code does not necessarily indicate that something went wrong but is returned if
    // the user cancels the authentication or if the user must restart the login process.
    if (authError.error !== 'interaction_required') {
      Sentry.captureException(authError);
    }
  }

  return Promise.resolve();
}

async function getToken(): Promise<string> {
  if (!auth) {
    throw new Error('Authentication not initialized');
  }

  try {
    if (!auth.authenticated) {
      // The user will no longer be authenticated after a period of time (unknown time period).
      // Thus, authenticate the user again.
      await initialize();
    }

    return await auth.getToken();
  } catch {
    await initialize();

    return 'NO-TOKEN';
  }
}

/**
 * The parsed token from the Keycloak token.
 */
let parsedToken: TokenParsed | undefined;

/**
 * Get the last parsed token without triggering a re-authentication.
 *
 * Note that this token may be outdated and might not be valid to use as authentication.
 *
 * @warning Do not use this function directly. Use `getAuth().getTokenParsed()` instead.
 * Otherwise, you risk getting an outdated token.
 */
function getStaleTokenParsed(): TokenParsed | undefined {
  return parsedToken;
}

/**
 * Get the current parsed token.
 *
 * If the user is not authenticated, the user will be re-authenticated.
 */
function getTokenParsed(): TokenParsed {
  if (!auth) {
    throw new Error('Authentication not initialized');
  }

  if (!auth.authenticated) {
    // The user will no longer be authenticated after a period of time (unknown time period).
    // Thus, authenticate the user again.
    // This is an async operation, but we can't return a promise here since it is used
    // in react hooks. Thus, we return the parsed token from the previous authentication.
    // The `initialize` function will force a re-authentication so it is safe
    // to return the previous token data for the few seconds before the user is
    // redirected to the login page.
    initialize();

    return parsedToken!;
  }

  if (!auth.tokenParsed) {
    throw new Error('Parsed token is missing');
  }

  const hasBehalfOf = !!auth.tokenParsed.on_behalf_of;

  // There is only a behalf of when the user is logged in to a specific organization.
  const behalfOf = hasBehalfOf
    ? {
        cmId: getOrganizationCmIdFromToken(auth.tokenParsed)!,
      }
    : undefined;

  parsedToken = {
    name: auth.tokenParsed.name,
    cmId: auth.tokenParsed.cm_id,
    signature: auth.tokenParsed.signature,

    // The email field is blank for external users.
    // Only internal users has their email exposed.
    email: auth.tokenParsed.email,

    amr: auth.tokenParsed.amr!,

    behalfOf,
  };

  return parsedToken;
}

function logout(): void {
  const webappRoot = `${window.location.protocol}//${
    window.location.host
  }${getBasename()}/`;

  if (!auth) {
    throw new Error('Authentication not initialized');
  }

  auth.logout({
    redirectUri: isInternalUser()
      ? webappRoot
      : window.config.logoutRedirectUrl,
  });
}

function isAuthenticated(): boolean {
  return auth?.authenticated ?? false;
}

/**
 * Storebrand authentication using Keycloak.
 */
export const storebrandAuth: Auth = {
  initialize,
  getToken,
  getTokenParsed,
  getStaleTokenParsed,
  logout,
  isAuthenticated,
};
